import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Grid } from '@mui/material';
import classNames from 'classnames';
import { AppFeatures } from 'components/GrowthBookWrapper/GrowthBookWrapper';
import ProfileImage from 'components/ProfileImage/ProfileImage';
import RatingStars from 'components/Rating/Stars/Stars';
import Tooltip from 'components/Tooltip/Tooltip';
import Button from 'components/ui-kit/Button/Button';
import Modal from 'components/ui-kit/Modal/Modal';
import InfoIcon from 'public/icons/icn-info.svg';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { palette } from 'themes/default';
import { GTM, GTMEvents } from 'utils/analytics';
import ImageUrlResizer from 'utils/image-url-resizer';

import { SHOP } from '../../config/fe-routes';
import useUser from '../../hooks/useUser';
import ShopFeedbackService from '../../services/shop-feedback.service';
import { ShopType } from '../../types/shop.types';
import { ShopFeedbackPublic } from '../../types/shop-feedback.types';
import { UserType } from '../../types/user.types';
import formatDate from '../../utils/format-date';
import { useNotification } from '../../utils/snackbar';
import A from '../Link/Link';
import ShopFeedbackForm from './ShopFeedbackForm';
import { ShopFeedbackItem } from './ShopFeedbackItem';
import { ShopFeedbackReply } from './ShopFeedbackReply';

export enum ShopFeedbackActionType {
  REVIEW_ADDED = 'REVIEW_ADDED',
  REVIEW_UPDATED = 'REVIEW_UPDATED',
  REVIEW_DELETED = 'REVIEW_DELETED',

  REPLY_ADDED_OR_UPDATED = 'REPLY_ADDED_OR_UPDATED',
  REPLY_DELETED = 'REPLY_DELETED',
}

export interface ShopFeedbackProps {
  feedback: ShopFeedbackPublic;
  orderId?: number;
  feedbackSender: (Partial<UserType> & { shop: Partial<ShopType> }) | null;
  feedbackReceiver: (Partial<UserType> & { shop: Partial<ShopType> }) | null;
  onClickSender?: () => void;
  onRefresh?: (
    feedback: ShopFeedbackPublic,
    action: ShopFeedbackActionType,
  ) => void; /** Automatic reviews already reported as seen in this shop visit; when given, the card reports itself. */
  trackedAutomaticReviewIds?: Set<number>;
}

const shopFeedbackService = new ShopFeedbackService();

export const ShopFeedback = ({
  feedback, //
  feedbackSender,
  feedbackReceiver,
  onClickSender,
  orderId,
  onRefresh,
  trackedAutomaticReviewIds,
}: ShopFeedbackProps) => {
  const { t } = useTranslation();
  const { showSuccessNotification } = useNotification();
  const { user } = useUser();
  const showReviewedRoleTag = useFeatureIsOn<AppFeatures>('feedback_role_tag_visible');

  const [isEdit, setEdit] = useState(!feedback?.rating);
  const [rating, setRating] = useState(feedback?.rating ?? 0);
  const [review, setReview] = useState(feedback?.review ?? '');
  const [error, setError] = useState(null);
  const feedbackOwner = user?.shop?.id === feedbackSender?.shop?.id;

  const [isSubmitting, setSubmitting] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const isAutomaticFeedback = !!feedback?.automatic;

  // Automatic reviews are written by Yaga, so Yaga is the sender. The new card (DEV-2612) sits
  // behind the same GrowthBook feature as the item box and the role tag: it uses the card wording
  // and adds an info icon that explains what automatic reviews are.
  const showNewAutomaticCard = isAutomaticFeedback && showReviewedRoleTag;

  // The item box names who sold or bought; it sits behind the review details flag.
  const showItemBox = showReviewedRoleTag && !!feedback?.item;

  const reviewText: string | undefined =
    feedback?.review ||
    (isAutomaticFeedback && feedback?.autoReviewReason
      ? t(`components.feedback.${showNewAutomaticCard ? 'auto-reason-card' : 'auto-reason'}.${feedback.autoReviewReason}`)
      : undefined);

  const reviewedShopId = feedbackReceiver?.shop?.id;

  // An automatic review in the new card counts as shown the first time at least half of it is on
  // screen, once per review per shop visit (DEV-2612). Same rule as the app.
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    const feedbackId = feedback?.id;

    if (!showNewAutomaticCard || !feedbackId || !trackedAutomaticReviewIds || !element || trackedAutomaticReviewIds.has(feedbackId)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || entry.intersectionRatio < 0.5 || trackedAutomaticReviewIds.has(feedbackId)) {
          return;
        }
        trackedAutomaticReviewIds.add(feedbackId);
        GTM.logEvent(GTMEvents.AUTOMATIC_REVIEW_SHOWN, { shop_id: reviewedShopId, feedback_id: feedbackId });
        observer.disconnect();
      },
      { threshold: 0.5 },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [showNewAutomaticCard, feedback?.id, trackedAutomaticReviewIds, reviewedShopId]);

  useEffect(() => {
    if (feedback?.rating) {
      setRating(feedback.rating);
    }
    if (feedback?.review) {
      setReview(feedback.review);
    }
  }, [feedback?.rating, feedback?.review]);

  const onCancelEdit = () => {
    setEdit(false);
    setRating(feedback?.rating ?? 0);
    setReview(feedback?.review ?? '');
  };

  const onSendFeedback = async () => {
    setSubmitting(true);

    try {
      // Create new.
      if (!feedback?.id) {
        const updatedShopFeedback = await shopFeedbackService.addShopFeedback(feedbackReceiver?.shop?.id, {
          orderId,
          rating,
          review,
        });

        onRefresh?.(updatedShopFeedback, ShopFeedbackActionType.REVIEW_ADDED);
      } else {
        // Update existing.
        const updatedShopFeedback = await shopFeedbackService.updateShopFeedback(feedback.id, {
          rating,
          review,
        });

        onRefresh?.(updatedShopFeedback, ShopFeedbackActionType.REVIEW_UPDATED);
      }

      setEdit(false);
      showSuccessNotification(t('components.feedback.success'));
    } catch (error) {
      if (error?.data?.data?.errorCode === 'FEEDBACK_EDIT_LIMIT_REACHED') {
        setError(t('components.feedback.errors.edit-limit'));
      } else if (error?.data?.data?.errorCode === 'VALIDATION_ERROR' && !!error?.data?.data?.errors?.rating) {
        setError(t('components.feedback.errors.missing-rating'));
      } else {
        setError(error?.data?.data?.msg || t('common.general-error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDeleteFeedback = async () => {
    if (!feedback?.id) {
      return;
    }

    setSubmitting(true);

    try {
      const deletedShopFeedback = await shopFeedbackService.deleteShopFeedback(feedback.id);

      setRating(0);
      setReview('');
      setEdit(true);
      showSuccessNotification(t('components.feedback.delete-success'));
      onRefresh?.(deletedShopFeedback, ShopFeedbackActionType.REVIEW_DELETED);
    } catch (_) {
      // Do nothing.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div ref={containerRef} className="shop-feedback-container" key={'shop-feedback-' + feedback?.id}>
        {/* Header row: avatar + name + stars + date */}
        <div className="shop-feedback-header-row">
          {!isAutomaticFeedback ? (
            <A className="shop-feedback-link no-style" {...SHOP(feedbackSender?.shop?.activeSlug)} onClick={() => onClickSender?.()}>
              <ProfileImage size={50} src={ImageUrlResizer.resize(feedbackSender?.picture, 100)} />
            </A>
          ) : (
            <ProfileImage size={50} src={'/icons/notifications/icn-yaga-round.svg'} />
          )}

          <div className="shop-feedback-header-info">
            <div className={classNames('shop-feedback-name', { automatic: isAutomaticFeedback })}>
              {!isAutomaticFeedback ? (
                <A className="shop-feedback-link no-style" {...SHOP(feedbackSender?.shop?.activeSlug)} onClick={() => onClickSender?.()}>
                  {feedbackSender?.shop?.name}
                </A>
              ) : (
                'Yaga'
              )}
            </div>

            <div className="shop-feedback-header">
              {/* Stars - interactive in edit mode, static otherwise. */}
              <div className="shop-feedback-stars">
                {isEdit ? (
                  <RatingStars
                    rating={rating}
                    color={palette.colorGray80}
                    onRate={(r) => {
                      setRating(r);
                      setError(null);
                    }}
                  />
                ) : (
                  <RatingStars rating={feedback?.rating} color={palette.colorGray80} />
                )}
              </div>

              {/* Date */}
              {feedback?.createdAt && <div className="shop-feedback-date">{formatDate(feedback.createdAt, true)}</div>}
            </div>
          </div>
        </div>

        {(reviewText || feedback?.reply || isEdit || feedbackOwner || showItemBox) && (
          <div className="shop-feedback">
            {/* Rating error in edit mode. */}
            {isEdit && error && <div className="shop-feedback-rating-error">{error}</div>}

            {/* Review text - view mode only. An automatic review in the new card gets an info icon
                that explains what automatic reviews are. */}
            {!isEdit && reviewText && (
              <div className={classNames('shop-feedback-text', { 'shop-feedback-text--with-info': showNewAutomaticCard })}>
                <div>{reviewText}</div>
                {showNewAutomaticCard && (
                  <Tooltip
                    title={`<strong>${t('components.feedback.automatic-review-title')}</strong><br /><br />${t(
                      'components.feedback.automatic-review-body-1',
                    )}<br /><br />${t('components.feedback.automatic-review-body-2')}<br /><br />${t(
                      'components.feedback.automatic-review-body-3',
                    )}`}
                    disableTitleTranslation
                    disableHoverStyle
                    iconClassName="shop-feedback-info-icon"
                    onOpen={() =>
                      GTM.logEvent(GTMEvents.AUTOMATIC_REVIEW_INFO_OPENED, { shop_id: reviewedShopId, feedback_id: feedback.id })
                    }
                  >
                    <InfoIcon />
                  </Tooltip>
                )}
              </div>
            )}

            {/* Item box - what the review is about. */}
            {!isEdit && showItemBox && (
              <ShopFeedbackItem
                item={feedback.item}
                role={feedback.reviewedRole}
                reviewerName={feedbackSender?.shop?.name}
                shopName={feedbackReceiver?.shop?.name}
                automatic={isAutomaticFeedback}
              />
            )}

            {/* Edit form - edit mode only. */}
            {isEdit && <ShopFeedbackForm review={review} setReview={setReview} isSubmitting={isSubmitting} />}

            {feedbackOwner && (
              <div className={classNames('feedback-actions', { 'in-edit-mode': isEdit })}>
                {isEdit || !feedback?.id ? (
                  <Grid container gap={1} flexWrap="nowrap">
                    {!!feedback?.id && (
                      <Grid item>
                        <Button buttonType="outlined" size="small" onClick={() => onCancelEdit()}>
                          {t('components.feedback.edit-cancel')}
                        </Button>
                      </Grid>
                    )}

                    <Grid item>
                      <Button buttonType="primaryOutlined" size="small" disabled={isSubmitting} onClick={() => onSendFeedback()}>
                        {t('components.feedback.submit')}
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container gap={1} flexWrap="nowrap">
                    {/* Reviews can only be edited once; the API says when the edit is used up. */}
                    {feedback?.canEditReview !== false && (
                      <Grid item>
                        <Button buttonType="outlined" size="small" onClick={() => setEdit(true)}>
                          {t('components.feedback.edit')}
                        </Button>
                      </Grid>
                    )}

                    <Grid item>
                      <Button buttonType="danger" size="small" onClick={() => setDeleteModalOpen(true)}>
                        {t('components.feedback.delete')}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </div>
            )}

            {/* Reply. */}
            <ShopFeedbackReply
              feedback={feedback}
              feedbackReceiver={feedbackReceiver}
              onClickSender={onClickSender}
              onRefresh={onRefresh}
            />
          </div>
        )}
      </div>

      <Modal
        open={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t('components.feedback.delete-confirm.title')}
        className="shop-feedback-delete-modal"
        actions={
          <>
            <Button buttonType="outlined" onClick={() => setDeleteModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              buttonType="primary"
              disabled={isSubmitting}
              onClick={async () => {
                await onDeleteFeedback();
                setDeleteModalOpen(false);
              }}
            >
              {t('components.feedback.delete-confirm.yes-delete')}
            </Button>
          </>
        }
      >
        {t('components.feedback.delete-confirm.message')}
      </Modal>
    </>
  );
};
